import React, { useState } from 'react';
import { Menu, Spin } from 'antd';
import ApplicantData from '@components/app-components/applicant/applicant-details/applicant-data/ApplicantData';
import { ApplicantElement } from '@models/applicants';
import { useTranslation } from 'next-i18next';
import styles from './ApplicantDetails.module.scss';
import AvatarIcon from '@assets/svgs/avatar-icon.svg';
import LaptopIcon from '@assets/svgs/laptop-icon.svg';
import dayjs from 'dayjs';
import useNotification from '@hooks/notification/useNotification';
import StateEnum from '@constants/state.enum';
import { getApplicantEmailTemplates } from '@services/applicants/applicants-http.service';
import { SentEmailApplicantElement } from '@models/email-templates';
import HtmlRenderer from '@components/ka-ui/html-renderer/HtmlRenderer';

type ApplicantDetailsProps = {
  applicant: ApplicantElement;
  fetchApplicant: () => void;
  fetchApplicants?: () => void;
};
const ApplicantDetails: React.FC<ApplicantDetailsProps> = ({ applicant, fetchApplicant, fetchApplicants }) => {
  const { t: translate } = useTranslation('applicant');
  const [current, setCurrent] = useState<string>('data');
  const [emails, setEmails] = useState<Array<SentEmailApplicantElement>>();

  const [generalError] = useNotification(
    StateEnum.ERROR,
    'Something went wrong',
    'Oops, something went wrong, please try again later!',
  );

  const fetchApplicantEmailTemplates = async () => {
    try {
      const emailsElem: Array<SentEmailApplicantElement> = await getApplicantEmailTemplates(applicant.id.toString());
      setEmails(emailsElem);
    } catch (e: any) {
      generalError();
    }
  };

  const tabsMenu = [
    {
      key: 'data',
      label: translate('data'),
    },
    {
      key: 'emails',
      label: `${translate('emails_tab')} (${applicant.emailCounter})`,
    },
  ];

  const tabs = (
    <Menu
      selectedKeys={[current]}
      mode="horizontal"
      className="menu-container"
      onClick={async (e) => {
        await fetchApplicantEmailTemplates();
        setCurrent(e.key);
      }}
      items={tabsMenu}
    />
  );

  const applicantsEmails = () => {
    if (emails) {
      return emails.map((email: SentEmailApplicantElement) => {
        return (
          <div className={styles.wrapEmailSentCard} key={email.body} data-testid="email-cards">
            <div className={styles.wrapUserIcon}>
              {email.lastName ? (
                <AvatarIcon style={{ height: '32px' }} />
              ) : (
                <LaptopIcon style={{ 'margin-top': '3px' }} />
              )}
            </div>
            <div className={styles.wrapEmailCardContent}>
              <div className={styles.wrapEmailTitle}>
                <span className={`h5 ${styles.templateTitle}`}>
                  {email.lastName ? `${email.firstName} ${email.lastName}` : translate('system_email')}
                </span>
                <span className={`small-caps-regular ${styles.templateDate}`}>
                  {dayjs(email.createdDate).format('DD MMM YYYY')}
                </span>
                {email.positionTitle && (
                  <>
                    <span> •</span>
                    <span className={styles.titleField}>{translate('position')}</span>
                    <span className={styles.contentPositionField}>{email.positionTitle}</span>
                  </>
                )}
                {email.stepName && (
                  <>
                    <span> •</span>
                    <span className={styles.titleField}>{translate('step')}</span>
                    <span className={styles.contentStepField}>{email.stepName ? email.stepName : '-'}</span>
                  </>
                )}
              </div>
              <div className={styles.wrapEmailContent}>
                <div>
                  <span className={styles.subjectTitleField}>{translate('subject_email')}</span>
                  <span className={styles.subjectContentField}>{email.subject}</span>
                  <div className={styles.separator}></div>
                  <div className={styles.renderer}>{email.body ? <HtmlRenderer html={email.body} /> : ''}</div>
                </div>
              </div>
            </div>
          </div>
        );
      });
    } else {
      return <Spin />;
    }
  };

  return (
    <div className={styles.wrapApplicantDetails}>
      {tabs}
      {current === 'data' ? (
        <ApplicantData
          applicant={applicant}
          fetchApplicant={() => fetchApplicant()}
          fetchApplicants={() => {
            if (fetchApplicants) fetchApplicants();
          }}
        />
      ) : (
        <>{applicantsEmails()}</>
      )}
    </div>
  );
};

export default ApplicantDetails;
